/**
 * The fixed lesson courses. Each lesson adds a few keys and practises everything
 * unlocked so far — the home-row-first method TypingClub and typing.com teach.
 * `keys` are US-QWERTY characters (what the key prints on a US board); the stage
 * builder turns a lesson's cumulative key set into drills and real words.
 *
 * English order: TypingClub's rows (home → top → bottom → shift → numbers).
 * Bijoy order: the data-driven order from the research (most-used keys first,
 * pre-base ি introduced in lesson 2, the ref/phala/conjunct rules near the end).
 */

export const ENGLISH_LESSONS = [
    { id: 'en-1', keys: 'fj', title: 'F J', focus: 'home' },
    { id: 'en-2', keys: 'fjdk', title: 'D K', focus: 'home' },
    { id: 'en-3', keys: 'fjdksl', title: 'S L', focus: 'home' },
    { id: 'en-4', keys: 'fjdksla;', title: 'A ;', focus: 'home' },
    { id: 'en-5', keys: 'fjdksla;gh', title: 'G H', focus: 'home' },
    { id: 'en-6', keys: 'fjdksla;ghrue i'.replace(' ', ''), title: 'R U E I', focus: 'top' },
    { id: 'en-7', keys: 'fjdksla;ghrueiwo', title: 'W O', focus: 'top' },
    { id: 'en-8', keys: 'fjdksla;ghrueiwoqpty', title: 'Q P T Y', focus: 'top' },
    { id: 'en-9', keys: 'fjdksla;ghrueiwoqptyvmc', title: 'V M C', focus: 'bottom' },
    { id: 'en-10', keys: 'fjdksla;ghrueiwoqptyvmcznb', title: 'Z N B', focus: 'bottom' },
    { id: 'en-11', keys: 'fjdksla;ghrueiwoqptyvmcznb,.', title: ', .', focus: 'bottom' },
    { id: 'en-12', keys: 'fjdksla;ghrueiwoqptyvmcznb,./x', title: 'X /', focus: 'bottom' },
    { id: 'en-13', keys: 'abcdefghijklmnopqrstuvwxyz,.', title: 'All letters', focus: 'review', words: true },
    { id: 'en-14', keys: 'abcdefghijklmnopqrstuvwxyz,.', title: 'Capitals (Shift)', focus: 'shift', caps: true, words: true },
    { id: 'en-15', keys: 'abcdefghijklmnopqrstuvwxyz,.?!', title: 'Punctuation', focus: 'punct', words: true, sentences: true },
    { id: 'en-16', keys: '1234567890', title: 'Numbers', focus: 'number', numbers: true },
    { id: 'en-17', keys: 'abcdefghijklmnopqrstuvwxyz,.?!', title: 'Sentences', focus: 'paragraph', words: true, sentences: true },
];

export const BIJOY_LESSONS = [
    { id: 'bn-1', keys: 'fj', title: 'া ক', focus: 'home' },
    { id: 'bn-2', keys: 'fjdk', title: 'ি ত', focus: 'home', note: 'preBase' },
    { id: 'bn-3', keys: 'fjdkhl', title: 'ব দ', focus: 'home' },
    { id: 'bn-4', keys: 'fjdkhlsa', title: 'ু ৃ', focus: 'home' },
    { id: 'bn-5', keys: 'fjdkhlsag', title: '্ link · আ ই উ', focus: 'home', note: 'link' },
    { id: 'bn-6', keys: 'fjdkhlsagcv', title: 'ে র · ো', focus: 'bottom', note: 'oKar' },
    { id: 'bn-7', keys: 'fjdkhlsagcvbm', title: 'ন ম', focus: 'bottom' },
    { id: 'bn-8', keys: 'fjdkhlsagcvbmnx', title: 'স ও', focus: 'bottom' },
    { id: 'bn-9', keys: 'fjdkhlsagcvbmnxrwt', title: 'প য ট', focus: 'top' },
    { id: 'bn-10', keys: 'fjdkhlsagcvbmnxrwtyuio', title: 'চ জ হ গ', focus: 'top' },
    { id: 'bn-11', keys: 'fjdkhlsagcvbmnxrwtyuioepq', title: 'ড ড় ঙ', focus: 'top' },
    { id: 'bn-12', keys: 'fjdkhlsagcvbmnxrwtyuioepqVWM', title: 'ল য় শ', focus: 'shift' },
    { id: 'bn-13', keys: 'fjdkhlsagcvbmnxrwtyuioepqVWMJKLH', title: 'খ থ ধ ভ', focus: 'shift' },
    { id: 'bn-14', keys: 'fjdkhlsagcvbmnxrwtyuioepqVWMJKLHDSFG', title: 'ী ূ অ ।', focus: 'shift' },
    { id: 'bn-15', keys: 'fjdkhlsagcvbmnxrwtyuioepqVWMJKLHDSFGYRNB', title: 'ছ ফ ষ ণ', focus: 'shift' },
    { id: 'bn-16', keys: 'fjdkhlsagcvbmnxrwtyuioepqVWMJKLHDSFGYRNBOETUIPCX', title: 'ঘ ঢ ঠ ঝ ঞ ৈ ৌ', focus: 'shift' },
    { id: 'bn-17', keys: 'fjdkhlsagcvbmnxrwtyuioepqVWMJKLHDSFGYRNBOETUIPCXZzAQ|&\\', title: '্য ্র র্ ং ঃ ঁ ৎ', focus: 'advanced', note: 'phala', words: true },
    { id: 'bn-18', keys: 'ALL', title: 'যুক্তাক্ষর (conjuncts)', focus: 'conjunct', conjuncts: true, words: true },
    { id: 'bn-19', keys: 'ALL', title: 'সংখ্যা ও যতিচিহ্ন', focus: 'number', numbers: true },
    { id: 'bn-20', keys: 'ALL', title: 'বাক্য ও অনুচ্ছেদ', focus: 'paragraph', words: true, sentences: true },
];

/**
 * Avro Phonetic: taught by sound, not by keyboard row. Each lesson adds a few
 * letters (keys are the latin characters typed, capitals included where they mean a
 * different letter); the words come from the Bangla list, kept to those the
 * unlocked letters can spell. The `o` lesson comes early because it is what keeps
 * two consonants apart (কত = kot, ক্ত = kt).
 */
export const AVRO_LESSONS = [
    { id: 'av-1', keys: 'akm', title: 'a k m · া ক ম', focus: 'sound', drill: true },
    { id: 'av-2', keys: 'akmit', title: 'i t · ি ত', focus: 'sound', drill: true },
    { id: 'av-3', keys: 'akmitrn', title: 'r n · র ন', focus: 'sound', drill: true },
    { id: 'av-4', keys: 'akmitrno', title: 'o · অ (কত = kot)', focus: 'sound', drill: true },
    { id: 'av-5', keys: 'akmitrnoel', title: 'e l · ে ল', focus: 'sound', drill: true },
    { id: 'av-6', keys: 'akmitrnoelbd', title: 'b d · ব দ', focus: 'sound', drill: true },
    { id: 'av-7', keys: 'akmitrnoelbdsu', title: 's u · স ু', focus: 'sound', drill: true },
    { id: 'av-8', keys: 'akmitrnoelbdsuh', title: 'h · হ খ থ ধ ভ', focus: 'aspirate', drill: true },
    { id: 'av-9', keys: 'akmitrnoelbdsuhgp', title: 'g p · গ প ঘ ফ', focus: 'aspirate', drill: true },
    { id: 'av-10', keys: 'akmitrnoelbdsuhgpjcy', title: 'j c y · জ চ য়', focus: 'aspirate' },
    { id: 'av-11', keys: 'akmitrnoelbdsuhgpjcyOIU', title: 'O I U · ো ী ূ', focus: 'shift' },
    { id: 'av-12', keys: 'akmitrnoelbdsuhgpjcyOIUTDNRS', title: 'T D N R S · ট ড ণ ড় শ', focus: 'shift' },
    { id: 'av-13', keys: 'akmitrnoelbdsuhgpjcyOIUTDNRSfvzwYZ', title: 'f v z w · ফ ভ য ব-ফলা', focus: 'shift', words: true },
    { id: 'av-14', keys: 'akmitrnoelbdsuhgpjcyOIUTDNRSfvzwYZG`:^', title: 'ng : ^ · ং ঃ ঁ ৎ', focus: 'advanced', words: true },
    { id: 'av-15', keys: 'ALL', title: 'যুক্তাক্ষর (kt = ক্ত)', focus: 'conjunct', conjuncts: true, words: true },
    { id: 'av-16', keys: 'ALL', title: 'সংখ্যা ও যতিচিহ্ন', focus: 'number', numbers: true },
    { id: 'av-17', keys: 'ALL', title: 'বাক্য ও অনুচ্ছেদ', focus: 'paragraph', words: true, sentences: true },
];

/** Common Bangla conjuncts for the conjunct drill, most frequent first. */
export const BIJOY_CONJUNCTS = [
    'ন্ত', 'ন্দ', 'ক্ষ', 'স্ত', 'ক্ত', 'ত্র', 'প্র', 'ন্য', 'ষ্ট', 'ম্ব', 'জ্ঞ', 'ঞ্চ', 'ঙ্গ', 'ক্র', 'গ্র',
    'ত্ত', 'দ্দ', 'দ্ব', 'দ্ধ', 'ন্ন', 'ম্ম', 'ল্ল', 'শ্ব', 'স্ব', 'স্প', 'স্ক', 'ব্দ', 'ব্ধ', 'ক্স', 'হ্ম',
    'ঠ্য', 'ত্য', 'দ্য', 'ন্ধ', 'ম্প', 'ম্ভ', 'র্থ', 'ষ্ঠ', 'ষ্ণ', 'স্থ', 'স্ম', 'স্ন', 'জ্জ', 'চ্ছ', 'ত্ন',
];

const COURSES = { bijoy: BIJOY_LESSONS, avro: AVRO_LESSONS, english: ENGLISH_LESSONS };
export const courseFor = (layoutId) => COURSES[layoutId] || ENGLISH_LESSONS;
